import { FirebaseAuthentication, User, AuthStateChange } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import './firebase';

type SignInResult = {
  user?: any | null;
  idToken?: string | null;
};

let initialized = false;
let authStateChangeListeners: Array<(user: any | null) => void> = [];
let webAuthRestoredResolve: ((u: any | null) => void) | null = null;
let webAuthRestoredPromise: Promise<any | null> | null = null;

export async function initAuth(): Promise<void> {
  if (initialized) {
    return;
  }
  initialized = true;

  // Native (Capacitor) platforms: use the Capacitor Firebase plugin listeners
  if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
    await FirebaseAuthentication.removeAllListeners();

    await FirebaseAuthentication.addListener('idTokenChange', async () => {
      const current = await FirebaseAuthentication.getCurrentUser();
      const user = current.user ?? null;

      authStateChangeListeners.forEach((cb) => cb(user));
    });
    return;
  }

  try {
    const authModule = await import('firebase/auth');
    const { getAuth, onAuthStateChanged, setPersistence, browserLocalPersistence } = authModule;
    const auth = getAuth();

    webAuthRestoredPromise = new Promise((res) => {
      webAuthRestoredResolve = res;
    });

    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch (e) {}

    onAuthStateChanged(auth, (u) => {
      authStateChangeListeners.forEach((cb) => cb(u ?? null));
      if (webAuthRestoredResolve) {
        try {
          webAuthRestoredResolve(u ?? null);
        } finally {
          webAuthRestoredResolve = null;
        }
      }
    });
  } catch (e) {
    // If firebase/auth isn't available for any reason, fall back to plugin behavior
    await FirebaseAuthentication.removeAllListeners();

    await FirebaseAuthentication.addListener('idTokenChange', async () => {
      const current = await FirebaseAuthentication.getCurrentUser();
      const user = current.user ?? null;

      authStateChangeListeners.forEach((cb) => cb(user));
    });
  }
}

async function safeGetIdToken(): Promise<string | null> {
  try {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      const current = await FirebaseAuthentication.getCurrentUser();
      if (!current.user) return null;

      const result = await FirebaseAuthentication.getIdToken();
      return result.token ?? null;
    }

    // web fallback
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      if (!auth.currentUser) return null;
      const token = await auth.currentUser.getIdToken();
      return token ?? null;
    } catch (e) {
      return null;
    }
  } catch {
    return null;
  }
}

export async function signInEmailPassword(email: string, password: string): Promise<SignInResult> {
  await initAuth();

  if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
    const result = await FirebaseAuthentication.signInWithEmailAndPassword({
      email,
      password,
    });

    if (!result.user) {
      return { user: null, idToken: null };
    }

    const idToken = await safeGetIdToken();

    return { user: result.user, idToken };
  }

  try {
    const { getAuth, signInWithEmailAndPassword } = await import('firebase/auth');
    const auth = getAuth();
    const res = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await res.user.getIdToken();
    return { user: res.user as any, idToken };
  } catch (e) {
    return { user: null, idToken: null };
  }
}

export async function signInWithGoogle(): Promise<SignInResult> {
  await initAuth();

  if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
    const result = await FirebaseAuthentication.signInWithGoogle();

    return {
      user: result.user ?? null,
      idToken: result.credential?.idToken ?? (await safeGetIdToken()),
    };
  }

  try {
    const { getAuth, GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    const res = await signInWithPopup(auth, provider);
    const idToken = await res.user.getIdToken();
    return { user: res.user as any, idToken };
  } catch (e) {
    return { user: null, idToken: null };
  }
}

export async function signOut(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signOut();
      return;
    }

    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    await auth.signOut();
  } catch {}
}

export async function getCurrentUser(): Promise<any | null> {
  try {
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.getCurrentUser();
      return result.user ?? null;
    }

    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    return (auth.currentUser as any) ?? null;
  } catch {
    return null;
  }
}

export function onAuthStateChanged(callback: (user: any | null) => void) {
  authStateChangeListeners.push(callback);

  (async () => {
    try {
      if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
        const u = await getCurrentUser();
        callback(u ?? null);
        return;
      }

      if (webAuthRestoredPromise) {
        const u = await webAuthRestoredPromise;
        callback(u ?? null);
        return;
      }

      const u = await getCurrentUser();
      callback(u ?? null);
    } catch (e) {
      callback(null);
    }
  })();

  return () => {
    authStateChangeListeners = authStateChangeListeners.filter((cb) => cb !== callback);
  };
}

export default {
  initAuth,
  signInEmailPassword,
  signInWithGoogle,
  signOut,
  getCurrentUser,
  onAuthStateChanged,
};
